import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const ActionButton = styled.button`
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #3367d6;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
const BackButton = styled(ActionButton)`
  background-color: #f5f5f5;
  color: #333;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const ControlsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 300px;
  font-size: 14px;
`;

const RefreshButton = styled.button`
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #3367d6;
  }
`;

const CreateReportButton = styled(ActionButton)`
  background-color: #34a853;

  &:hover {
    background-color: #2e8b57;
  }
`;

const ReportsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f9f9f9;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }

  cursor: pointer;
`;

const TableHeader = styled.th`
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #333;
`;

const TableCell = styled.td`
  padding: 15px;
  color: #555;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const NoDataContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => props.color || "#e0e0e0"};
  color: ${(props) => props.textColor || "#333"};
`;

function ReportsListPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    fetchReports();
  }, []);

  const goBack = () => {
    navigate("/");
  };
  const fetchReports = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiUrl}/api/reports/all`);
      if (response.data.success) {
        // Trier les rapports par date de création (plus récent en premier)
        const sortedReports = response.data.reports.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setReports(sortedReports);
      } else {
        setError("Impossible de récupérer les rapports");
      }
    } catch (err) {
      setError(`Erreur lors du chargement des rapports: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchReports();
  };

  const handleRowClick = (reportId) => {
    navigate(`/dashboard/${reportId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case "xandr":
        return (
          <Badge color="#e8f0fe" textColor="#1a73e8">
            Xandr
          </Badge>
        );
      case "csv":
        return (
          <Badge color="#fce8e6" textColor="#d93025">
            CSV
          </Badge>
        );
      default:
        return <Badge>Inconnu</Badge>;
    }
  };
  const handleCreateReport = () => {
    navigate("/create-sms-email-report");
  };

  const filteredReports = reports.filter(
    (report) =>
      report.advertiserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.campaignName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={goBack}>← Retour</BackButton>
        <Title>Liste des rapports générés</Title>
        <Subtitle>Consultez et gérez tous vos rapports de campagne</Subtitle>
      </Header>

      <ControlsBar>
        <SearchInput
          type="text"
          placeholder="Rechercher par annonceur ou campagne..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div style={{ display: "flex", gap: "10px" }}>
          <CreateReportButton onClick={handleCreateReport}>
            + Créer un rapport SMS/Email
          </CreateReportButton>
          <RefreshButton onClick={handleRefresh}>
            <span>↻</span> Actualiser
          </RefreshButton>
        </div>
      </ControlsBar>

      {error && <ErrorContainer>{error}</ErrorContainer>}

      {loading ? (
        <LoadingContainer>Chargement des rapports...</LoadingContainer>
      ) : filteredReports.length === 0 ? (
        <NoDataContainer>
          {searchTerm
            ? "Aucun rapport ne correspond à votre recherche"
            : "Aucun rapport disponible"}
        </NoDataContainer>
      ) : (
        <ReportsTable>
          <TableHead>
            <tr>
              <TableHeader>Annonceur</TableHeader>
              <TableHeader>Campagne</TableHeader>
              <TableHeader>Période</TableHeader>
              <TableHeader>Lignes</TableHeader>
              <TableHeader>Source</TableHeader>
              <TableHeader>Créé le</TableHeader>
            </tr>
          </TableHead>
          <tbody>
            {filteredReports.map((report) => (
              <TableRow
                key={report.id}
                onClick={() => handleRowClick(report.id)}
              >
                <TableCell>{report.advertiserName || "N/A"}</TableCell>
                <TableCell>{report.campaignName || "N/A"}</TableCell>
                <TableCell>
                  {report.dateRange
                    ? `${
                        formatDate(report.dateRange.startDate).split(",")[0]
                      } - ${formatDate(report.dateRange.endDate).split(",")[0]}`
                    : "N/A"}
                </TableCell>
                <TableCell>{report.rowCount}</TableCell>
                <TableCell>{getSourceBadge(report.source)}</TableCell>
                <TableCell>{formatDate(report.createdAt)}</TableCell>
              </TableRow>
            ))}
          </tbody>
        </ReportsTable>
      )}
    </PageContainer>
  );
}

export default ReportsListPage;
